import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AccountService } from '@app/_services/account.service';
import { TokenRefreshService } from '@app/_services/auth/tokenRefresh.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { from, Observable, retry, throwError, TimeoutError, timer } from 'rxjs';
import { catchError, switchMap, timeout } from 'rxjs/operators';
import { RequestCancelledError } from './requestCancelledError';

export const TIMEOUT_TOKEN = new HttpContextToken<number>(() => 15_000);
export const SLOW_REQUEST_TIMEOUT = 300_000;

const MAX_RETRIES = 3; // upper bound; the delay fn decides per error type whether to actually retry
const TIMEOUT_RETRY_DELAY_MS = 1_000;
const BACKOFF_BASE_DELAY_MS = 1_000;
const MAX_BACKOFF_DELAY_MS = 30_000; // safety cap in case MAX_RETRIES is raised

const isSafeToRetry = (request: HttpRequest<unknown>) => ['GET', 'HEAD'].includes(request.method);

export class RequestTimeoutError extends Error {
  constructor(request: HttpRequest<unknown>) {
    super(`${request.method} ${request.url} timed out`);
    this.name = 'RequestTimeoutError';
  }
}

// Marks a retried request so a second 401 goes straight to logout instead of another refresh attempt.
const IS_RETRY = new HttpContextToken<boolean>(() => false);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly accountService = inject(AccountService);
  private readonly oauthService = inject(OAuthService);
  private readonly tokenRefreshService = inject(TokenRefreshService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const canRetry = isSafeToRetry(request);

    return next.handle(request).pipe(
      timeout(request.context.get(TIMEOUT_TOKEN)),
      retry({
        count: MAX_RETRIES,
        delay: (error, retryCount) => {
          // retry timeouts only once
          if (canRetry && error instanceof TimeoutError) {
            return retryCount === 1 ? timer(TIMEOUT_RETRY_DELAY_MS) : throwError(() => error);
          }

          // 502–504 are fast failures (e.g. during a deployment) — retry with exponential backoff.
          if (canRetry && error instanceof HttpErrorResponse && error.status >= 502) {
            return timer(Math.min(BACKOFF_BASE_DELAY_MS * 2 ** (retryCount - 1), MAX_BACKOFF_DELAY_MS));
          }

          return throwError(() => error);
        },
      }),
      catchError((error: HttpErrorResponse) => {
        if (error instanceof TimeoutError) {
          return throwError(() => new RequestTimeoutError(request));
        }
        // Requests canceled by the browser during logout produce status 0. Map them to a typed
        // error that the ErrorHandlerService and Sentry ignore, so they don't surface as
        // "backend offline" dialogs or Sentry events. Completing empty instead would make
        // firstValueFrom() consumers reject with "EmptyError: no elements in sequence".
        if (error.status === 0) {
          return throwError(() => new RequestCancelledError(error));
        }
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Don't refresh or force-logout for requests to external services, e.g., to Keycloaks account endpoint
          if (!request.url?.startsWith('/')) {
            return throwError(() => error);
          }
          if (!request.context.get(IS_RETRY)) {
            return this.handle401(request, next, error);
          }
          // A freshly refreshed token was rejected as well — the session is unusable.
          this.accountService.logout();
        }

        return throwError(() => error as HttpErrorResponse);
      }),
    );
  }

  private handle401(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    originalError: HttpErrorResponse,
  ): Observable<HttpEvent<unknown>> {
    return from(this.tokenRefreshService.refresh()).pipe(
      catchError(() => throwError(() => originalError)),
      // Re-enter this interceptor so the retry gets the timeout and error mapping, and a second 401 logs out.
      switchMap(() => this.intercept(this.cloneWithFreshToken(request), next)),
    );
  }

  private cloneWithFreshToken(request: HttpRequest<unknown>): HttpRequest<unknown> {
    // The context object is shared between a request and its clones. Copy it, so marking the retry doesn't
    // also mark the original: if a caller resends it (e.g. with retry()), a 401 must trigger a refresh again.
    const context = new HttpContext();
    for (const token of request.context.keys()) {
      context.set(token, request.context.get(token));
    }

    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${this.oauthService.getAccessToken()}`),
      context: context.set(IS_RETRY, true),
    });
  }
}
